import React from 'react'
import { useS4LContext } from '@s4l/react-context'

import { IconText, SelectableOption, Typography, Tag } from '@s4l/ds'
import { Insurance } from '@s4l/stayforlong/booking/Insurance'

import styles from './styles.module.css'
import { IconCheck } from '@s4l/icons/IconCheck'
import { IconShield } from '@s4l/icons/IconShield'
import { IconTime } from '@s4l/icons/IconTime'
import { Market } from '@s4l/stayforlong/shared/Market'

import { type UtilityClassNames } from '@s4l/utility-css'

export interface IInsuranceSelectorProps {
  insuranceJSON?: ReturnType<Insurance['toJSON']>
  onClick?: (value: IInsuranceSelectorType) => void
  selected: IInsuranceSelectorType
  subtitle?: string
  error?: boolean
}

export type IInsuranceSelectorType = {
  isInsured: boolean | null
  availabilityId?: string
}

const COVERAGE_I18N_IDS = [
  'insurance.accident-injury',
  'insurance.transport-failure',
  'insurance.vehicle-failure',
  'insurance.redundancy',
  'insurance.home-emergency',
  'insurance.relative-sickness',
  'insurance.medical-conditions',
  'insurance.and-more',
]

function InsuranceSelector({
  insuranceJSON,
  onClick,
  selected,
  subtitle,
  error,
}: Readonly<IInsuranceSelectorProps>) {
  const { i18n } = useS4LContext()

  const insurance = Insurance.fromJSONOrUndefined(insuranceJSON)
  if (!insurance) return null
  if (!insurance.hasInsurance() || !insurance.market) return null

  const market = Market.fromJSON({ value: insurance.market })

  const price = insurance.getPriceFormatted(i18n.locale, market)

  const getVariant = (isSelectedValue: boolean) => {
    return (
      (selected.isInsured === isSelectedValue && 'selected') ||
      (selected.isInsured !== null && 'default') ||
      (error && 'error') ||
      'default'
    )
  }

  const terms = insurance.getInsuranceTerms()?.value

  const handleClick = (value: IInsuranceSelectorType) => {
    onClick?.(value)
  }

  return (
    <div className={styles.insuranceSelector} data-testid="insurance-selector">
      <div className={styles.titleWrapper}>
        <div className={styles.title}>
          <IconShield />
          <Typography as="h6" variant="title" size="xs">
            {i18n.formatMessage({ id: 'insurance.make-refundable' })}
          </Typography>
        </div>
        <div className={styles.tagWrapper}>
          <Tag size="m">{i18n.formatMessage({ id: 'global.recommended' })}</Tag>
        </div>
      </div>

      {subtitle && (
        <Typography
          className={styles.subtitle as UtilityClassNames}
          size="l"
          color="var(--color-neutral-800)"
          bold
        >
          {subtitle}
        </Typography>
      )}

      <div className={styles.descriptions}>
        <ul>
          {COVERAGE_I18N_IDS.map(coverageMessageId => (
            <li key={coverageMessageId}>
              <IconText
                icon={<IconCheck color="var(--color-success-500)" />}
                color="var(--color-neutral-600)"
                wrap
              >
                {i18n.formatMessage({ id: coverageMessageId })}
              </IconText>
            </li>
          ))}
        </ul>

        <Typography variant="caption" size="l" color="var(--color-neutral-600)">
          {i18n.formatMessage(
            { id: 'insurance.no-stress' },
            {
              terms: (
                <a href={terms} target="_blank" rel="noopener noreferrer">
                  {i18n.formatMessage({ id: 'insurance.terms-refund' })}
                </a>
              ),
            }
          )}
        </Typography>

        <IconText
          icon={<IconTime size={24} color="var(--color-success-500)" />}
          color="var(--color-success-500)"
          wrap
          bold
        >
          {i18n.formatMessage({ id: 'insurance.refund-approval' })}
        </IconText>
      </div>
      <div className={styles.options}>
        <SelectableOption
          title={i18n.formatMessage({ id: 'insurance.make-refundable' })}
          value="insured"
          onClick={() => handleClick({ isInsured: true, availabilityId: insurance.id })}
          variant={getVariant(true)}
        >
          <div className={styles.priceWrapper}>
            <Typography variant="title" size="xs" bold>
              {price}
            </Typography>
          </div>
        </SelectableOption>

        <SelectableOption
          title={i18n.formatMessage({ id: 'insurance.non-refundable' })}
          value="notInsured"
          onClick={() => handleClick({ isInsured: false, availabilityId: insurance.id })}
          variant={getVariant(false)}
        />
      </div>
    </div>
  )
}

InsuranceSelector.displayName = 'InsuranceSelector'
export default InsuranceSelector
